const FREE_LINE_PRICE = 0;
const PARENT_ID_PROPERTY = "_p_id";
const LINK_TO_PARENT_PROPERTY = "_p_id_link";
const PRICE_PLACEHOLDER = "[price]";
const LINE_PRICE_DISPLAY = "line";

window._sections = window._sections || {};

const isFreeLine = (line) => line?.final_line_price === FREE_LINE_PRICE;

const isLinkedToParent = (line) => !!line?.properties?.[LINK_TO_PARENT_PROPERTY];

const getShownLinePrice = (line, priceDisplay) =>
  (priceDisplay === LINE_PRICE_DISPLAY ? line?.final_line_price : line?.final_price) ?? 0;

const buildPriceComparer = (priceDisplay) => (first, second) => {
  if (isFreeLine(first) !== isFreeLine(second)) return isFreeLine(first) ? 1 : -1;
  return getShownLinePrice(second, priceDisplay) - getShownLinePrice(first, priceDisplay);
};

const findParentLine = (line, lines) => {
  if (!isLinkedToParent(line)) return null;
  const parentId = line.properties[LINK_TO_PARENT_PROPERTY];
  return (
    lines.find(
      (candidate) => !isLinkedToParent(candidate) && candidate?.properties?.[PARENT_ID_PROPERTY] === parentId
    ) ?? null
  );
};

const groupLinesUnderParents = (lines) => {
  const topLines = [];
  const childLinesByParentKey = new Map();
  lines.forEach((line) => {
    const parentLine = findParentLine(line, lines);
    if (!parentLine) {
      topLines.push(line);
      return;
    }
    const childLines = childLinesByParentKey.get(parentLine.key) ?? [];
    childLinesByParentKey.set(parentLine.key, [...childLines, line]);
  });
  return { topLines, childLinesByParentKey };
};

const getCartLinesInDisplayOrder = (lines, priceDisplay) => {
  const compareLinesByPrice = buildPriceComparer(priceDisplay);
  const { topLines, childLinesByParentKey } = groupLinesUnderParents(lines ?? []);
  return [...topLines]
    .sort(compareLinesByPrice)
    .flatMap((line) => [line, ...[...(childLinesByParentKey.get(line.key) ?? [])].sort(compareLinesByPrice)]);
};

const getUpgradeVariant = (upgradeProduct) =>
  upgradeProduct?.variants?.find((variant) => variant.id === upgradeProduct.selected_or_first_available_variant_id);

const UPGRADE_LINE_KEY = "upgrade";

const buildUpgradeLine = (line, upgradeProduct, upgradeVariant) => ({
  key: UPGRADE_LINE_KEY,
  product_id: upgradeProduct.id,
  variant_id: upgradeVariant.id,
  quantity: line.quantity,
  original_line_price: upgradeVariant.price * line.quantity,
  properties: {},
});

const getUpgradePriceDifference = (line, upgradeProduct) => {
  const upgradeVariant = getUpgradeVariant(upgradeProduct);
  if (!upgradeVariant || !line) return 0;
  const ladder = window._sections?.getBundleLadder?.();
  if (!ladder?.findCartPriceChangeForSwap) return upgradeVariant.price - line.original_price;
  return ladder.findCartPriceChangeForSwap(line, buildUpgradeLine(line, upgradeProduct, upgradeVariant));
};

const isProductInCart = (productId) =>
  (window._stores?.cart?.state?.items ?? []).some((cartLine) => cartLine.product_id === productId && cartLine.quantity > 0);

const isUpgradeHiddenByCart = (product) =>
  (product?.upgrade_hidden_by_product_ids ?? []).some(isProductInCart);

const isUpgradeOffered = (line, product, upgradeProduct) =>
  !!product?.upgrade_id &&
  !isUpgradeHiddenByCart(product) &&
  product.upgrade_id !== line?.product_id &&
  upgradeProduct?.id === product.upgrade_id &&
  !!upgradeProduct?.title &&
  !!upgradeProduct?.selected_or_first_available_variant_id;

const getUpgradeButtonText = (line, upgradeProduct, texts, formatPrice) => {
  const priceDifference = getUpgradePriceDifference(line, upgradeProduct);
  if (priceDifference <= 0) return texts.button_without_price;
  return texts.button_with_price.replace(PRICE_PLACEHOLDER, formatPrice(priceDifference));
};

const hasSameProperties = (firstLine, secondLine) =>
  JSON.stringify(firstLine?.properties ?? {}) === JSON.stringify(secondLine?.properties ?? {});

const findLineToReplace = (cartLines, line) =>
  cartLines.find((cartLine) => cartLine.key === line.key) ??
  cartLines.find((cartLine) => cartLine.variant_id === line.variant_id && hasSameProperties(cartLine, line)) ??
  cartLines.find((cartLine) => cartLine.variant_id === line.variant_id);

const BUNDLE_FLAG_PROPERTY = "_bundle";

const getUpgradeLineProperties = (line, upgradeProduct) => {
  const ladder = window._sections?.getBundleLadder?.();
  const ladderProperties = ladder?.bundleProperties?.(upgradeProduct?.id) ?? {};
  const bundleFlag = ladder?.dealsAreEnabled ? line?.properties?.[BUNDLE_FLAG_PROPERTY] : null;
  const keptBundleFlag = bundleFlag ? { [BUNDLE_FLAG_PROPERTY]: bundleFlag } : {};
  return { ...keptBundleFlag, ...ladderProperties };
};

const replaceCartLineWithUpgrade = async (line, upgradeProduct) => {
  const lineToReplace = { key: line?.key, variant_id: line?.variant_id, properties: { ...(line?.properties ?? {}) } };
  const lineQuantity = line?.quantity || 1;
  const upgradeVariantId = upgradeProduct?.selected_or_first_available_variant_id;
  if (!lineToReplace.key || !upgradeVariantId) return false;

  const cartStore = window._stores.cart;
  cartStore.loading = true;
  try {
    const cartAfterAdd = await window._cart.add({
      items: [{ id: upgradeVariantId, quantity: lineQuantity, properties: getUpgradeLineProperties(line, upgradeProduct) }],
    });
    if (cartAfterAdd?.cart_error) return false;
    const currentLine = findLineToReplace(cartAfterAdd?.items ?? [], lineToReplace);
    if (!currentLine) return false;
    await window._cart.change({ id: currentLine.key, quantity: 0 });
    return true;
  } finally {
    cartStore.loading = false;
  }
};

const CART_SCROLL_CONTAINER_SELECTOR = "[data-cart-drawer-checkout-form]";

const shownUpgradeElements = new WeakSet();

const refreshCartScroll = (element) => {
  const scrollContainer = element?.closest(CART_SCROLL_CONTAINER_SELECTOR);
  if (!scrollContainer) return;
  const scrollTop = scrollContainer.scrollTop;
  scrollContainer.style.overflowY = "hidden";
  requestAnimationFrame(() => {
    scrollContainer.style.overflowY = "";
    scrollContainer.scrollTop = scrollTop;
  });
};

const refreshCartScrollWhenUpgradeShows = (element, isShown) => {
  if (!isShown) {
    shownUpgradeElements.delete(element);
    return;
  }
  if (shownUpgradeElements.has(element)) return;
  shownUpgradeElements.add(element);
  requestAnimationFrame(() => refreshCartScroll(element));
};

window._sections["getCartLinesInDisplayOrder"] = getCartLinesInDisplayOrder;
window._sections["refreshCartScrollWhenUpgradeShows"] = refreshCartScrollWhenUpgradeShows;
window._sections["replaceCartLineWithUpgrade"] = replaceCartLineWithUpgrade;
window._sections["isUpgradeOffered"] = isUpgradeOffered;
window._sections["getUpgradeButtonText"] = getUpgradeButtonText;
